import { Link } from "react-router-dom";
import { LegalLayout } from "@/components/legal/LegalLayout";
import { BUSINESS } from "@/lib/legal";

const COLUMNS = ["Finalidad", "Datos tratados", "Base legal", "Conservación"];

const Table = ({ rows }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
        <tr>
          {COLUMNS.map((c) => <th key={c} className="px-4 py-2.5 font-semibold">{c}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r) => (
          <tr key={r[0]} className="align-top">
            {r.map((c, i) => <td key={COLUMNS[i]} className="px-4 py-3 text-slate-600">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const sections = [
  {
    id: "responsable",
    title: "Responsable del tratamiento",
    content: (
      <>
        <p>
          Conforme al Reglamento (UE) 2016/679 (RGPD) y a la Ley Orgánica 3/2018, de Protección de Datos Personales y garantía de
          los derechos digitales (LOPDGDD), le informamos de que el responsable del tratamiento de sus datos es:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Responsable:</strong> <span className="notranslate">{BUSINESS.name}</span> — NIF <span className="notranslate">{BUSINESS.nif}</span></li>
          <li><strong>Dirección:</strong> {BUSINESS.address}</li>
          <li><strong>Correo de contacto para protección de datos:</strong> {BUSINESS.email}</li>
        </ul>
      </>
    ),
  },
  {
    id: "finalidades",
    title: "Finalidades, datos tratados y base legal",
    content: (
      <>
        <p>Tratamos sus datos personales únicamente para las finalidades que se describen a continuación:</p>
        <Table rows={[
          ["Gestionar reservas y pedidos online (recogida, lavado, entrega y pago)", "Nombre, correo, teléfono, dirección de recogida, fecha, notas, servicios contratados, importe y estado del pago", "Ejecución del contrato (art. 6.1.b RGPD) y obligación legal en materia fiscal y de facturación (art. 6.1.c)", "Durante la relación contractual y, después, los plazos legales de prescripción (hasta 6 años en materia contable/fiscal)"],
          ["Calcular la distancia y el coste de desplazamiento", "Dirección de recogida (se envía a servicios cartográficos para geolocalizarla)", "Ejecución del contrato (art. 6.1.b)", "Mientras dure la gestión del pedido"],
          ["Cuenta de cliente (inicio de sesión con Google)", "Nombre, correo, foto de perfil, teléfono y dirección habitual que decida guardar, historial de pedidos", "Ejecución del contrato / medidas precontractuales (art. 6.1.b)", "Mientras mantenga la cuenta activa; puede solicitar su supresión en cualquier momento"],
          ["Avisos sobre el estado del pedido por correo electrónico", "Nombre, correo, número y estado del pedido", "Ejecución del contrato (art. 6.1.b)", "Mientras dure la gestión del pedido"],
          ["Atender consultas del formulario de contacto o WhatsApp", "Nombre, correo o teléfono y contenido del mensaje", "Consentimiento (art. 6.1.a) e interés legítimo en responder (art. 6.1.f)", "Hasta resolver la consulta y, como máximo, 1 año"],
          ["Publicar opiniones y valoraciones en la web", "Nombre o alias, puntuación y comentario", "Consentimiento (art. 6.1.a)", "Mientras no retire su consentimiento o solicite su eliminación"],
          ["Seguridad y prevención del fraude", "Dirección IP, registros de acceso, intentos de inicio de sesión", "Interés legítimo (art. 6.1.f)", "Máximo 12 meses"],
          ["Mostrar publicidad (Google AdSense)", "Identificadores de cookies publicitarias, dirección IP, datos de navegación en esta web", "Consentimiento (art. 6.1.a) para anuncios personalizados; sin consentimiento se muestran anuncios no personalizados", "Según la duración de cada cookie (ver Política de cookies), máximo 13 meses"],
        ]} />
        <p>No elaboramos perfiles ni tomamos decisiones automatizadas con efectos jurídicos. No enviamos comunicaciones comerciales sin su consentimiento previo.</p>
      </>
    ),
  },
  {
    id: "destinatarios",
    title: "Destinatarios y transferencias internacionales",
    content: (
      <>
        <p>Sus datos pueden ser comunicados a los siguientes encargados o terceros, en la medida imprescindible para prestar el servicio:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Stripe Payments Europe, Ltd.</strong> — procesamiento del pago con tarjeta. Nunca recibimos ni almacenamos el número completo de su tarjeta.</li>
          <li><strong>Google LLC</strong> — inicio de sesión con cuenta de Google, mapa de ubicación, traducción automática de la web y publicidad mediante Google AdSense (Google actúa como responsable independiente del tratamiento de los datos publicitarios).</li>
          <li><strong>Proveedor de envío de correo electrónico transaccional</strong> — envío de confirmaciones y avisos de estado.</li>
          <li><strong>OpenStreetMap Foundation (Nominatim) y proyecto OSRM</strong> — geolocalización de la dirección y cálculo de la distancia por carretera. Solo se envía la dirección, sin nombre ni datos de contacto.</li>
          <li><strong>Proveedor de alojamiento e infraestructura</strong> de la web y la base de datos.</li>
          <li><strong>Administraciones públicas</strong>, cuando exista obligación legal.</li>
        </ul>
        <p>
          Algunos de estos proveedores pueden estar ubicados fuera del Espacio Económico Europeo (por ejemplo, en Estados Unidos). En
          tales casos, las transferencias se amparan en decisiones de adecuación de la Comisión Europea (Marco de Privacidad de Datos
          UE-EE. UU.) o en cláusulas contractuales tipo.
        </p>
      </>
    ),
  },
  {
    id: "derechos",
    title: "Sus derechos",
    content: (
      <>
        <p>
          Puede ejercer en cualquier momento sus derechos de <strong>acceso, rectificación, supresión, oposición, limitación del
          tratamiento y portabilidad</strong>, así como retirar el consentimiento otorgado, escribiendo a {BUSINESS.email} o por correo
          postal a {BUSINESS.address}, indicando el derecho que desea ejercer y adjuntando una copia de un documento identificativo.
          Responderemos en el plazo máximo de un mes.
        </p>
        <p>
          Si considera que sus derechos no han sido debidamente atendidos, puede presentar una reclamación ante la Agencia Española de
          Protección de Datos (<a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-cyan-700 underline underline-offset-2">www.aepd.es</a>), C/ Jorge Juan 6, 28001 Madrid.
        </p>
      </>
    ),
  },
  {
    id: "seguridad",
    title: "Seguridad de los datos",
    content: (
      <p>
        Aplicamos medidas técnicas y organizativas adecuadas para proteger sus datos: comunicaciones cifradas (HTTPS), contraseñas
        del personal almacenadas de forma cifrada, bloqueo ante intentos de acceso repetidos, acceso restringido al panel de gestión
        y pagos gestionados íntegramente por un proveedor certificado PCI-DSS.
      </p>
    ),
  },
  {
    id: "menores",
    title: "Menores de edad",
    content: (
      <p>
        Nuestros servicios online están dirigidos a personas mayores de 18 años. Si detectamos que hemos recogido datos de un menor sin
        la autorización de sus padres o tutores, procederemos a eliminarlos.
      </p>
    ),
  },
  {
    id: "cookies",
    title: "Cookies",
    content: (
      <p>
        Utilizamos cookies técnicas y de preferencias. Puede consultar toda la información en nuestra{" "}
        <Link to="/cookies" className="text-cyan-700 underline underline-offset-2">Política de cookies</Link>.
      </p>
    ),
  },
  {
    id: "cambios",
    title: "Cambios en esta política",
    content: (
      <p>
        Podemos actualizar esta Política de privacidad para adaptarla a novedades legislativas o a cambios en nuestros servicios. La
        versión vigente estará siempre publicada en esta página con su fecha de última actualización.
      </p>
    ),
  },
];

export default function PoliticaPrivacidad() {
  return (
    <LegalLayout
      testId="legal-privacidad-page"
      title="Política de privacidad"
      subtitle="Cómo tratamos y protegemos tus datos personales cuando reservas, te registras, nos escribes u opinas."
      sections={sections}
    />
  );
}
